import { Drawing } from '@editor/interfaces/drawing'
import { Matrix4, Vector3 } from 'three'

/**
 * Get the highest number given a name and number string combo.
 * This is useful for generating unique names based on existing ones.
 * For example, if there's "延長 1", "延長 2", this will return "2".
 *
 * @param items - Array of item names to check against.
 * @param name - The base name to check against.
 * @returns
 */
export const getLastCount = (items: string[], name: string): number => {
  const counts = items
    .map((item) => {
      const match = item.match(`^${name}\\s*(\\d+)$`)
      return match ? parseInt(match[1], 10) : null
    })
    .filter((count): count is number => count !== null)

  return counts.length ? Math.max(...counts) : 0
}

/**
 * Get the new order number for a new item based on existing items.
 *
 * @param items
 */
export const getNewOrder = (items: Drawing[]): number =>
  items.length ? Math.max(...items.map((item) => item.drawing_parameters.order || 0)) + 1 : 0

/**
 * Creates a 2D representation of a 3D polygon and a matrix to map back.
 * @param points - points of the polygon in order (Vector3)
 */
export const createPolygonProjection = (
  points: Vector3[],
): {
  vertices: [number, number][]
  matrix: Matrix4 // 2D → 3D transform
} => {
  // Compute X axis from first edge
  const xAxis = new Vector3().subVectors(points[1], points[0]).normalize()

  // Compute normal
  const normal = new Vector3()
    .crossVectors(new Vector3().subVectors(points[1], points[0]), new Vector3().subVectors(points[2], points[0]))
    .normalize()

  // Compute Y axis (orthogonal)
  const yAxis = new Vector3().crossVectors(normal, xAxis).normalize()

  // Origin at first point
  const origin = points[0].clone()

  // Build local → world matrix
  const localToWorld = new Matrix4().makeBasis(xAxis, yAxis, normal)
  localToWorld.setPosition(origin)

  // World → local for projection
  const worldToLocal = new Matrix4().copy(localToWorld).invert()

  // Project points into 2D local space
  const points2D = points.map((p) => {
    const local = p.clone().applyMatrix4(worldToLocal)
    return [local.x, local.y] as [number, number] // ignore z
  })

  return { vertices: points2D, matrix: localToWorld }
}
